import { OAuthError, useAuth0 } from '@auth0/auth0-react'
import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { endSilentSsoLogin, getSilentSsoLoginReturnTo, getSsoConnection } from '../../utils/authSource'
import { SSO_LOGIN_FAILED } from '../../utils/loginErrors'
import { ViewLayout } from '../layouts/ViewLayout'
import { ComponentLayout } from '../layouts/ComponentLayout'
import { LoadingSpinner } from '../LoadingSpinner'

interface Props {
  children: React.ReactNode
}

const NO_SESSION_ERRORS = ['login_required', 'interaction_required']

const isNoSessionError = (error: Error | undefined): boolean =>
  error instanceof OAuthError && NO_SESSION_ERRORS.includes(error.error)

// Without this the authentication guard would send a failed SSO callback straight back to Auth0 in a loop
export const AuthErrorGate = ({ children }: Props) => {
  const { error, loginWithRedirect } = useAuth0()
  const { pathname } = useLocation()
  const [isFallingBackToSsoLogin, setIsFallingBackToSsoLogin] = useState(false)
  const silentSsoReturnTo = isNoSessionError(error) ? getSilentSsoLoginReturnTo() : null
  const shouldFallBackToSsoLogin = isFallingBackToSsoLogin || silentSsoReturnTo !== null
  const hasSsoLoginFailed = Boolean(error && getSsoConnection() && pathname !== '/error') && !shouldFallBackToSsoLogin

  useEffect(() => {
    if (silentSsoReturnTo === null) return

    setIsFallingBackToSsoLogin(true)
    endSilentSsoLogin()
    loginWithRedirect({ appState: { returnTo: silentSsoReturnTo } })
  }, [silentSsoReturnTo, loginWithRedirect])

  useEffect(() => {
    if (hasSsoLoginFailed) {
      // The error screen is generic, so keep the Auth0 error somewhere it can be found
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }, [hasSsoLoginFailed, error])

  if (shouldFallBackToSsoLogin) {
    return (
      <ViewLayout>
        <ComponentLayout>
          <LoadingSpinner />
        </ComponentLayout>
      </ViewLayout>
    )
  }

  if (hasSsoLoginFailed) {
    return <Navigate to="/error" state={{ errorMessage: SSO_LOGIN_FAILED }} replace />
  }

  return children
}
